import { motion } from 'framer-motion';
import { Music, Mic, Play, Pause, Volume2, Heart, Search, Filter } from 'lucide-react';
import { useEffect, useState, useMemo, useCallback } from 'react';

interface Song {
  id: number;
  title: string;
  artist: string;
  genre: string;
  duration: string;
  isFavorite: boolean;
}

interface KaraokeData {
  songs: Song[];
  total: number;
  lastUpdated: string;
}

const genres = ["Sve", "Pop", "Rock", "Indie Rock", "Klasična", "Narodna", "Razno"];

export function KaraokePage() {
  const [karaokeData, setKaraokeData] = useState<KaraokeData | null>(null);
  const [selectedGenre, setSelectedGenre] = useState("Sve");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [favorites, setFavorites] = useState<Set<number>>(new Set());
  
  const SONGS_PER_PAGE = 50;

  useEffect(() => {
    fetch('/karaoke.json')
      .then(response => response.json())
      .then((data: KaraokeData) => {
        setKaraokeData(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Greška pri učitavanju pjesama:', error);
        setLoading(false);
      });
  }, []);

  // Memoizirano filtriranje
  const filteredSongs = useMemo(() => {
    if (!karaokeData) return [];
    
    let filtered = karaokeData.songs;

    // Filtriranje po žanru
    if (selectedGenre !== "Sve") {
      filtered = filtered.filter(song => song.genre === selectedGenre);
    }

    // Filtriranje po pretrazi
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(song => 
        song.title.toLowerCase().includes(searchLower) ||
        song.artist.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [karaokeData, selectedGenre, searchTerm]);

  // Paginacija
  const paginatedSongs = useMemo(() => {
    const startIndex = (currentPage - 1) * SONGS_PER_PAGE;
    return filteredSongs.slice(startIndex, startIndex + SONGS_PER_PAGE);
  }, [filteredSongs, currentPage]);

  const totalPages = Math.ceil(filteredSongs.length / SONGS_PER_PAGE);

  const toggleFavorite = useCallback((songId: number) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(songId)) {
        newFavorites.delete(songId);
      } else {
        newFavorites.add(songId);
      }
      return newFavorites;
    });
  }, []);

  // Reset paginacije kada se promijeni filter
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedGenre, searchTerm]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Učitavam popis pjesama...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4"
            >
              <Mic className="w-8 h-8 text-white" />
            </motion.div>
            <h1 className="text-3xl md:text-4xl font-bold text-blue-900 mb-2">
              Karaoke
            </h1>
            <p className="text-gray-600 text-lg">
              Odaberite svoju omiljenu pjesmu i zapjevajte!
            </p>
          </div>

          {/* Filter Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-sm border border-blue-100 p-6 mb-8"
          >
            <h2 className="text-xl font-semibold text-blue-900 mb-4 flex items-center">
              <Music className="w-5 h-5 mr-2" />
              Filtriraj po žanru
            </h2>
            <div className="flex flex-wrap gap-2">
              {genres.map((genre) => (
                <button
                  key={genre}
                  onClick={() => setSelectedGenre(genre)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedGenre === genre
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-700'
                  }`}
                >
                  {genre}
                </button>
              ))}
            </div>
            
            {/* Search */}
            <div className="mt-4 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Pretraži pjesme..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </motion.div>

          {/* Songs List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl shadow-sm border border-blue-100 overflow-hidden"
          >
            <div className="bg-blue-600 text-white p-4">
              <h2 className="text-xl font-semibold flex items-center">
                <Play className="w-5 h-5 mr-2" />
                Popis pjesama ({filteredSongs.length} od {karaokeData?.total || 0})
              </h2>
              {totalPages > 1 && (
                <p className="text-blue-100 text-sm mt-1">
                  Stranica {currentPage} od {totalPages}
                </p>
              )}
            </div>

            <div className="divide-y divide-gray-100">
              {paginatedSongs.map((song, index) => (
                <motion.div
                  key={song.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="p-4 hover:bg-blue-50 transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      {/* Play Button */}
                      <button className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors group-hover:scale-105">
                        <Play className="w-4 h-4 ml-0.5" />
                      </button>

                      {/* Song Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {song.title}
                        </h3>
                        <p className="text-sm text-gray-600 truncate">
                          {song.artist}
                        </p>
                      </div>

                      {/* Genre Badge */}
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                        {song.genre}
                      </span>

                      {/* Duration */}
                      <span className="text-sm text-gray-500 w-12 text-right">
                        {song.duration}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      <button 
                        onClick={() => toggleFavorite(song.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Heart className={`w-5 h-5 ${favorites.has(song.id) ? 'fill-current text-red-500' : ''}`} />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                        <Volume2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Prethodna
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Sljedeća
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Prikazuje{' '}
                      <span className="font-medium">{(currentPage - 1) * SONGS_PER_PAGE + 1}</span>
                      {' '}do{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * SONGS_PER_PAGE, filteredSongs.length)}
                      </span>
                      {' '}od{' '}
                      <span className="font-medium">{filteredSongs.length}</span>
                      {' '}rezultata
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Prethodna</span>
                        ←
                      </button>
                      
                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Sljedeća</span>
                        →
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Info Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8 bg-gradient-to-r from-blue-50 to-white rounded-xl p-6 border border-blue-100"
          >
            <h3 className="text-lg font-semibold text-blue-900 mb-3">
              Kako koristiti karaoke?
            </h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
              <div>
                <p className="mb-2">🎤 Kliknite na play gumb pored pjesme</p>
                <p className="mb-2">🎵 Slijedite tekst na ekranu</p>
              </div>
              <div>
                <p className="mb-2">❤️ Označite omiljene pjesme</p>
                <p className="mb-2">🔊 Podesite glasnoću prema potrebi</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
